import { FC, FocusEventHandler, useEffect, useState } from 'react';
import { SelectChangeEvent } from '@mui/material/Select';

import Text from '../Text';
import { ORANGE, RED, TEXT } from '../../Helpers/colors';
import { SelectDataProperties } from '.';

import './chipSelect.scss';

interface ChipSelectProperties {
	className?: string;
	data: SelectDataProperties[] | null;
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	initialValue?: string[];
	label?: string;
	onBlur?: FocusEventHandler<HTMLDivElement>;
	onConfirm?: () => void;
	setValue: (event: SelectChangeEvent<string[]>) => void;
	warning?: boolean;
	warningMessage?: string;
}

const ChipSelect: FC<ChipSelectProperties> = ({
	data,
	setValue,
	onBlur,
	label = '',
	description = '',
	error = false,
	errorMessage = '',
	warning = false,
	warningMessage = '',
	className = '',
	initialValue = [],
	disabled = false,
	onConfirm,
}) => {
	const [values, setValues] = useState<string[]>([]);

	useEffect(() => {
		if (!initialValue?.length) return;
		setValues(initialValue);
	}, [initialValue]);

	const toggleChip = (id: string | number, isDefault?: boolean) => {
		if (disabled || isDefault) return;

		const idStr = String(id);
		const newValues = values.includes(idStr) ? values.filter((v) => v !== idStr) : [...values, idStr];

		setValues(newValues);

		const event = {
			target: { value: newValues, name: '' },
		} as SelectChangeEvent<string[]>;
		setValue(event);

		// The BT directly editing the chip selection is itself a confirmation of the value — the
		// "Confirm" prompt is only meant to flag AI-entered values the BT hasn't reviewed yet, so it
		// must clear as soon as the BT interacts with the field themselves.
		if (warning) onConfirm?.();
	};

	return (
		<div
			className={`chipSelect ${error ? 'chipSelect--error' : warning ? 'chipSelect--warning' : ''} ${className}`}
			onBlur={onBlur}
			tabIndex={-1}
			aria-invalid={error || undefined}
		>
			<div className="inputLabelRow" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
				<Text
					className={`inputLabel ${label ? 'active' : ''}`}
					title={label}
					type="psm"
					fontWeight="medium"
					color={disabled ? TEXT.QUATERNARY : TEXT.PRIMARY}
				/>
				{warning && onConfirm && (
					<button type="button" className="confirmChip" onClick={onConfirm}>
						Confirm
					</button>
				)}
			</div>
			<div className="chipSelect__chips">
				{data?.map((item) => {
					const isSelected = values.includes(String(item.id));
					const chipLabel = item.name ?? item.text ?? item.label ?? '';

					return (
						<button
							key={item.id}
							type="button"
							className={`chipSelect__chip ${isSelected ? 'chipSelect__chip--selected' : ''} ${
								disabled || item.isDefault ? 'chipSelect__chip--disabled' : ''
							}`}
							onClick={() => toggleChip(item.id, item.isDefault)}
							disabled={disabled}
							aria-pressed={isSelected}
						>
							{chipLabel}
						</button>
					);
				})}
			</div>
			{error && (
				<Text
					className="error active"
					title={errorMessage}
					fontWeight="medium"
					type="psm"
					color={RED.BASE}
				/>
			)}
			{!error && warning && (
				<Text
					className="warning active"
					title={warningMessage}
					fontWeight="medium"
					type="psm"
					color={ORANGE.BASE}
				/>
			)}
			{!error && !warning && Boolean(description) && (
				<Text
					className="inputDescription active"
					title={description}
					fontWeight="medium"
					type="psm"
				/>
			)}
		</div>
	);
};

export default ChipSelect;
